import React, { useEffect, useRef, useState } from 'react';
import { FaStar, FaQuoteLeft } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import '../styles/Testimonials.css';

const testimonials = [
  {
    id: 1,
    name: 'Anastasia Krostali',
    location: 'Thessaloniki',
    rating: 5,
    text: {
      el: 'Το πρώτο γλυκό. Ήρθα από Θεσσαλονίκη αποκλειστικά για τα γλυκά της Μάγιας και άξιζε το ταξίδι. Παιδιά δεν παίζεστε. Όσοι δεν τα έχετε δοκιμάσει δεν ξέρετε τι χάνετε.',
      en: 'The best dessert. I came from Thessaloniki specifically for Maya\'s desserts and it was worth the trip. It is truly incredible. Those of you who haven\'t tried them don\'t know what you\'re missing.'
    },
    isNew: true,
  },
  {
    id: 2,
    name: 'Eleftherios Pavlidis',
    location: 'Cyprus',
    rating: 5,
    text: {
      el: 'Εκπληκτικά γλυκά! Πολύ νόστιμα και απόλυτα υπέροχα! 🤩',
      en: 'Amazing desserts! Super tasty and absolutely gorgeous! 🤩'
    },
    badge: 'Local Guide',
  },
  {
    id: 3,
    name: 'Μαρία Δινάκη',
    location: 'Cyprus',
    rating: 5,
    text: {
      el: 'Μόνο εδώ βρίσκω τα πιο viral French fruits 🍊🍋🍏🥭',
      en: 'I only find the most viral French fruits here 🍊🍋🍏🥭'
    },
    badge: 'Local Guide',
  },
  {
    id: 4,
    name: 'Andria Charalambous',
    location: 'Cyprus',
    rating: 5,
    text: {
      el: 'Τα καλύτερα γλυκά μακράν μόνο στο Maya\'s Flavours 💗💗🫶🏼🔝',
      en: 'The best sweets by far only at Maya\'s Flavours 💗💗🫶🏼🔝'
    },
  },
  {
    id: 5,
    name: 'Nefeli Lamprinou',
    location: 'Cyprus',
    rating: 5,
    text: {
      el: 'Αξίζει η επίσκεψη! Γεύση και εξυπηρέτηση 10/10⭐️',
      en: 'Worth a visit! Taste and service 10/10⭐️'
    },
    isNew: true,
  },
  {
    id: 6,
    name: 'M&M CYBER IT LTD',
    location: 'Cyprus',
    rating: 5,
    text: {
      el: 'Τόσο νόστιμα και ικανοποιητικά! Τα γλυκά είναι μαλακά, γευστικά και υπέροχα φτιαγμένα.',
      en: 'So tasty and satisfying! The sweets are soft, flavourful, and beautifully made.'
    },
  },
];

function Testimonials() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
  const { language, t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setIsPaused(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const diff = e.clientX - startX;
    setTranslateX(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    const threshold = 50;
    if (translateX > threshold) {
      // Swiped right - go to previous
      setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    } else if (translateX < -threshold) {
      // Swiped left - go to next
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }
    
    setIsDragging(false);
    setTranslateX(0);
    setTimeout(() => setIsPaused(false), 3000); // Resume auto-play after 3s
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const diff = e.touches[0].clientX - startX;
    setTranslateX(diff);
  };

  const handleTouchEnd = () => {
    handleMouseUp();
  };

  const handleDotClick = (index) => {
    setActiveIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 3000);
  };

  return (
    <div className="testimonials-section" ref={sectionRef}>
      <div className="testimonials-container">
        <div className={`testimonials-header ${isVisible ? 'visible' : ''}`}>
          <h2 className="section-title">{t('testimonials.title')}</h2>
          <p className="section-subtitle">{t('testimonials.subtitle')}</p>
        </div>

        {/* Featured Testimonial Slider */}
        <div 
          className={`featured-testimonial ${isVisible ? 'visible' : ''} ${isDragging ? 'dragging' : ''}`}
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div 
            className="testimonial-slider"
            style={{ 
              transform: `translateX(${translateX}px)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease'
            }}
          >
            <div className="featured-quote-icon">
              <FaQuoteLeft />
            </div>
            <p className="featured-text">
              {testimonials[activeIndex].text[language]}
            </p>
            <div className="featured-author">
              <div className="author-avatar">
                {testimonials[activeIndex].name.charAt(0)}
              </div>
              <div className="author-info">
                <span className="author-name">
                  {testimonials[activeIndex].name}
                  {testimonials[activeIndex].isNew && <span className="new-badge">New</span>}
                  {testimonials[activeIndex].badge && <span className="guide-badge">{testimonials[activeIndex].badge}</span>}
                </span>
                <span className="author-location">{testimonials[activeIndex].location}</span>
              </div>
              <div className="author-rating">
                {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                  <FaStar key={i} className="star-icon" />
                ))}
              </div>
            </div>
          </div>
          
          {/* Swipe Hint */}
          <div className="swipe-hint">
            <span>← Swipe →</span>
          </div>
          
          {/* Navigation Dots */}
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === activeIndex ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Testimonial Cards Grid */}
        <div className={`testimonials-grid ${isVisible ? 'visible' : ''}`}>
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <div 
              key={testimonial.id}
              className="testimonial-card"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="card-rating">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} className="star-icon" />
                ))}
              </div>
              <p className="card-text">{testimonial.text[language]}</p>
              <div className="card-author">
                <div className="author-avatar small">
                  {testimonial.name.charAt(0)}
                </div>
                <div className="author-details">
                  <span className="author-name">{testimonial.name}</span>
                  <span className="author-location">{testimonial.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Background Decorations */}
      <div className="testimonials-decoration testimonials-deco-1"></div>
      <div className="testimonials-decoration testimonials-deco-2"></div>
    </div>
  );
}

export default Testimonials;
